/**
 * NewLeadDialog — Lead Capture form.
 *
 * Minimal, touch-friendly capture per spec:
 *   Name · Phone · WhatsApp (same-as-phone checkbox) · Destination ·
 *   Travel date range · Pax · Notes · Source
 *
 * Submits to POST /api/website-leads (status defaults to "new" = the Lead
 * stage). Auto-opens via `?new=1` (wired in Leads.js + the mobile FAB).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { invalidate } from '@/mobile/lib/cache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// value → label. Matches the spec's Source dropdown exactly.
const SOURCES = [
  { value: 'walk-in', label: 'Walk-in' },
  { value: 'phone', label: 'Phone call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'referral', label: 'Referral' },
  { value: 'website', label: 'Website' },
  { value: 'other', label: 'Other' },
];

const EMPTY = {
  name: '', phone: '', whatsapp: '', sameWa: true,
  destination: '', travelDate: '', travelEndDate: '',
  travellersCount: '', message: '', source: 'walk-in',
};

const NewLeadDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const reset = () => setForm(EMPTY);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name,
        phone: form.phone.trim(),
        whatsapp: (form.sameWa ? form.phone : form.whatsapp).trim(),
        destination: form.destination.trim(),
        travelDate: form.travelDate,
        travelEndDate: form.travelEndDate,
        travellersCount: form.travellersCount ? Number(form.travellersCount) : null,
        message: form.message.trim(),
        source: form.source,
      };
      const r = await axios.post(`${BACKEND_URL}/api/website-leads`, payload, { withCredentials: true });
      toast.success(`Lead ${r.data.leadId} added`);
      invalidate('/api/website-leads');
      reset();
      onOpenChange(false);
      onCreated?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-lead-dialog">
        <DialogHeader>
          <DialogTitle>New Lead</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input
              autoFocus
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="Customer full name"
              data-testid="lead-name-input"
              className="mt-1 h-11"
            />
          </div>

          <div>
            <Label className="text-xs">Phone</Label>
            <Input
              type="tel"
              value={form.phone}
              onChange={(e) => set({ phone: e.target.value })}
              placeholder="9876543210"
              data-testid="lead-phone-input"
              className="mt-1 h-11"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 mb-1.5 cursor-pointer select-none" data-testid="lead-same-wa-label">
              <Checkbox
                checked={form.sameWa}
                onCheckedChange={(v) => set({ sameWa: !!v })}
                data-testid="lead-same-wa-checkbox"
              />
              <span className="text-xs text-slate-600">WhatsApp same as phone</span>
            </label>
            {!form.sameWa && (
              <Input
                type="tel"
                value={form.whatsapp}
                onChange={(e) => set({ whatsapp: e.target.value })}
                placeholder="WhatsApp number"
                data-testid="lead-whatsapp-input"
                className="h-11"
              />
            )}
          </div>

          <div>
            <Label className="text-xs">Destination</Label>
            <Input
              value={form.destination}
              onChange={(e) => set({ destination: e.target.value })}
              placeholder="Bali, Maldives, …"
              data-testid="lead-destination-input"
              className="mt-1 h-11"
            />
          </div>

          <div>
            <Label className="text-xs">Travel dates</Label>
            <div className="grid grid-cols-2 gap-3 mt-1">
              <Input
                type="date"
                value={form.travelDate}
                onChange={(e) => set({ travelDate: e.target.value })}
                data-testid="lead-traveldate-input"
                className="h-11"
              />
              <Input
                type="date"
                value={form.travelEndDate}
                min={form.travelDate || undefined}
                onChange={(e) => set({ travelEndDate: e.target.value })}
                data-testid="lead-travelenddate-input"
                className="h-11"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Pax</Label>
              <Input
                type="number"
                min="1"
                inputMode="numeric"
                value={form.travellersCount}
                onChange={(e) => set({ travellersCount: e.target.value })}
                placeholder="2"
                data-testid="lead-pax-input"
                className="mt-1 h-11"
              />
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Select value={form.source} onValueChange={(v) => set({ source: v })}>
                <SelectTrigger className="mt-1 h-11" data-testid="lead-source-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`lead-source-${s.value}`}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea
              rows={2}
              value={form.message}
              onChange={(e) => set({ message: e.target.value })}
              placeholder="Special requirements, conversation context…"
              data-testid="lead-message-input"
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="lead-cancel-btn" className="min-h-[44px]">Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="lead-save-btn"
            className="bg-emerald-700 hover:bg-emerald-800 text-white min-h-[44px]"
          >
            {saving ? 'Adding…' : 'Add lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewLeadDialog;
